import { useState } from "react";
import { Link } from "wouter";
import { Users, ClipboardList, ArrowRight, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import type { AdminStats } from "../AdminDashboard";

interface RecentActivityProps {
  recentUsers: AdminStats["recentUsers"];
  recentAssessments: AdminStats["recentAssessments"];
}

const cardClass =
  "bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden";

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const statusStyle = (status: string) => {
  switch (status) {
    case "finalized": return { bg: "bg-emerald-50", text: "text-emerald-600", dot: "bg-emerald-500" };
    case "complete": return { bg: "bg-blue-50", text: "text-blue-600", dot: "bg-blue-500" };
    default: return { bg: "bg-slate-100", text: "text-slate-600", dot: "bg-slate-400" };
  }
};

export function RecentActivity({ recentUsers, recentAssessments }: RecentActivityProps) {
  const [usersExpanded, setUsersExpanded] = useState(true);
  const [assessmentsExpanded, setAssessmentsExpanded] = useState(true);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Recent Signups */}
      <div className={cardClass}>
        <div className="px-6 py-5 border-b border-border/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">{"Recent Signups"}</h3>
              <p className="text-sm text-slate-500">{"Latest users who joined the platform"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/admin/users">
              <span className="text-sm text-primary hover:underline flex items-center gap-1 font-medium">
                {"View all"} <ArrowRight className="h-3 w-3" />
              </span>
            </Link>
            <button
              onClick={() => setUsersExpanded((v) => !v)}
              className="p-1 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
              aria-label={usersExpanded ? "Collapse" : "Expand"}
            >
              {usersExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {usersExpanded && (
          <div className="p-6">
            {recentUsers.length === 0 ? (
              <div className="text-center py-4 space-y-2">
                <p className="text-sm text-slate-500">{"No signups yet"}</p>
                <Link href="/admin/invitations">
                  <span className="text-sm text-primary hover:underline flex items-center justify-center gap-1 font-medium">
                    {"Send first invitation"} <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentUsers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                        {u.firstName && u.lastName
                          ? `${u.firstName.charAt(0)}${u.lastName.charAt(0)}`
                          : u.email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                        </p>
                        <p className="text-xs text-slate-400">{u.companyName || u.role}</p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">{formatDate(u.createdAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent Assessments */}
      <div className={cardClass}>
        <div className="px-6 py-5 border-b border-border/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-[43px] h-[43px] bg-primary/10 rounded-2xl flex items-center justify-center">
              <ClipboardList className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">{"Recent Assessments"}</h3>
              <p className="text-sm text-slate-500">{"Latest assessment activity"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/admin/assessments">
              <span className="text-sm text-primary hover:underline flex items-center gap-1 font-medium">
                {"View all"} <ArrowRight className="h-3 w-3" />
              </span>
            </Link>
            <button
              onClick={() => setAssessmentsExpanded((v) => !v)}
              className="p-1 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
              aria-label={assessmentsExpanded ? "Collapse" : "Expand"}
            >
              {assessmentsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {assessmentsExpanded && (
          <div className="p-6">
            {recentAssessments.length === 0 ? (
              <div className="text-center py-4 space-y-2">
                <p className="text-sm text-slate-500">{"No assessments yet"}</p>
                <Link href="/admin/workspaces">
                  <span className="text-sm text-primary hover:underline flex items-center justify-center gap-1 font-medium">
                    {"Go to Workspaces"} <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentAssessments.map((a) => {
                  const ss = statusStyle(a.status);
                  return (
                    <div key={a.id} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="font-semibold text-slate-900">{a.organizationName}</p>
                        <p className="text-xs text-slate-400">{a.workspaceName || "-"}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-slate-100 rounded-full h-1.5">
                            <div
                              className="bg-primary h-1.5 rounded-full transition-all"
                              style={{ width: `${a.completenessPercent}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-400 w-8">{a.completenessPercent}%</span>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${ss.bg} ${ss.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
                          {a.status}
                        </span>
                        {a.status === "finalized" && (
                          <button
                            onClick={() => window.open(`/admin/reports/${a.id}`, "_blank")}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View Report
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
